import "./style.scss";

import { SubdirectoryArrowRight } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import { Avatar, Chip, Modal, Radio, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import AlertTriangle from "assets/img/svg/alertTriangle";
import Checkmark from "assets/img/svg/checkmark";
import { Info } from "assets/img/svg/info";
import OutlinedFile from "assets/img/svg/outlinedFile";
import Plus from "assets/img/svg/plus";
import { useAgreementVariables } from "components/ContractsSettings/ContractEditorLexical/variables";
import ModalBox from "components/Modal";
import { tagsColors } from "constants/colors";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { PortalType } from "contexts/portal.context";
import { Body, H3, Heading1, Heading2, PanelTitle, SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { clamp, downloadFile, getInitials, getStatementFilename, isObject, removeBrackets } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { SetStateAction } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserListByCompany } from "store/adminPanelSlice";
import type { AgreementTemplate } from "store/agreementsStore";
import {
    AgreementSignatureStatus,
    fetchAgreementGenerated,
    fetchAgreementsTemplate,
    postAgreement,
    sendAgreementsInBulk,
} from "store/agreementsStore";
import type { IArticle, ManagementArticle } from "store/articleStore";
import { getAllArticles, getAllArticlesBeneficiars, setContractSigned } from "store/articleStore";
import { fetchBeneficiar, fetchBeneficiaries, updateBeneficiary } from "store/beneficiaryStore";
import type { ITerm } from "store/managementStore";
import type { MainPortalType } from "store/settingsStore";
import { fetchContract, fetchContractDownload } from "store/statementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { IUser } from "types";
import { SubsetOf } from "types";

import variables from "../../../assets/styles/_variables.module.scss";
import AutocompleteFormComponent from "../FormComponent/AutocompleteFormComponent";
import InfoBlue from "../InfoBlockBlue";
import LastElementTable from "../LastElementTable";
import type { LastElementTableColumns } from "../LastElementTable/type";
import MultiStep from "../MultiStep";
import { FileButton } from "../MultiStep/ImportFile";
import { Spinner } from "../Spinner";
import StyledButton from "../StyledButton";
import StyledTextField from "../StyledTextField/StyledTextField";
import TextButton from "../TextButton";
import EmailModal from "./EmailModal";

export const VARIABLE_REGEX = /\{\{[^\s{}][\s\S]*?[^\s{}]\}\}/g; // follow this patern {{variableName}} or {{variable name}}, but no spaces like this {{ variableName }}
export const VARIABLE_PATTERN = "\\{\\{[^\\s{}][\\s\\S]*?[^\\s{}]\\}\\}";

// Matches span tag with data-text attribute containing a variable
export const SPAN_WITH_VARIABLE_REGEX = new RegExp(`<span[^>]*data-text="(${VARIABLE_PATTERN})"[^>]*>`, "g");

export type UserVariables = Record<string, string>;

/**
 * This agreement modal component is used for Press
 */
interface AgreementModalProps {
    /**
     * Article to sign, used mostly for the ids
     */
    article?: IArticle | ITerm;
    /**
     * Which portal the modal is called from
     */
    from: MainPortalType;
    open: boolean;
    /**
     * On modal close
     */
    onClose: () => void;
    /**
     * On agreement send
     */
    onSend?: () => void;
    /**
     * Items selected for sending in bulk.
     */
    articles?: IArticle[] | ITerm[];
    /**
     * Whether it is sent in bulk or not.
     */
    inBulk?: boolean;
    /**
     * On press portal, from which page is launched the AgreementModal
     */
    pageType?: "publication" | "beneficiary" | "management";
    /**
     * Related resource type
     */
    relatedResource: "article" | "term";
    /**
     * Pre-select document type on open
     */
    initialDocumentType?: "agreement" | "avenant";
}

// FOR PRESS
const AgreementModal = ({
    article,
    from,
    onClose,
    onSend = () => {},
    articles,
    inBulk = false,
    pageType,
    open,
    relatedResource,
    initialDocumentType,
}: AgreementModalProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const isBeneficiarsPage = location.pathname.includes("beneficiars");
    const { beneficiar } = useAppSelector((state) => state.beneficiary);
    const { agreementsTemplate, defaultAgreementTemplate } = useAppSelector((state) => state.agreements);
    const email = article?.email?.replace(/\s/g, "") || beneficiar?.email?.replace(/\s/g, "");
    const beneficiaryId =
        (["audiovisual", "press"].includes(from)
            ? (article as IArticle)?.beneficiaryId
            : (article as ITerm)?.beneficiarId) ||
        beneficiar?.id ||
        -1;
    const [userVariables, setUserVariables] = useState<UserVariables>();
    const { getCompanySettings } = useCompanySettings();
    const usesValidation = getCompanySettings("uses_agreement_validation_chain");

    const [activeStep, setActiveStep] = useState(0);
    const [templateId, setTemplateId] = useState(-1);
    const [hasSent, setHasSent] = useState(false);
    const [documentType, setDocumentType] = useState<"agreement" | "avenant">(initialDocumentType ?? "agreement");

    const resourceIds = articles?.map((article) => article.id);

    const allAgreementsTemplate = defaultAgreementTemplate
        ? ([defaultAgreementTemplate, ...agreementsTemplate] as AgreementTemplate[])
        : null;

    useEffect(() => {
        if (!isBeneficiarsPage && !inBulk) dispatch(fetchBeneficiar(beneficiaryId));
    }, [isBeneficiarsPage]);

    useEffect(() => {
        if (usesValidation) dispatch(fetchBeneficiaries({ limit: 100, offset: 0 }));
    }, [usesValidation]);

    const handleStepChange = (_: any, idx: number) => {
        setActiveStep(idx);
    };

    const handleStep1 = (templateId: number, userVariables?: UserVariables) => {
        setActiveStep(1);
        setTemplateId(templateId);
        setUserVariables(!userVariables ? {} : userVariables);
    };
    const handleStep2 = async (email?: string) => {
        if (article && !inBulk) {
            if (!email) return;
            const hasChangedEmail = beneficiar.email !== email;

            try {
                if (hasChangedEmail) {
                    await dispatch(
                        updateBeneficiary({
                            id: beneficiaryId,
                            data: { email },
                        }),
                    );
                }

                await dispatch(
                    postAgreement({
                        relatedResourceId: article?.id,
                        relatedResource,
                        templateId,
                        userVariables,
                        type: documentType,
                    }),
                );
                setActiveStep(2);
                dispatch(setContractSigned(AgreementSignatureStatus.SENT));
                if (pageType === "beneficiary") {
                    dispatch(getAllArticlesBeneficiars(beneficiaryId));
                }
                if (pageType === "publication" && article.titleId != null) {
                    dispatch(getAllArticles(article.titleId));
                }
            } catch (e) {
                setHasSent(false);
            }
        }

        // Different fetch when sending in bulk.
        if (inBulk) {
            if (resourceIds) {
                const response = await dispatch(
                    sendAgreementsInBulk({
                        templateId,
                        resourceIds,
                        relatedResource: "article",
                        userVariables,
                    }),
                );
                if (response.meta.requestStatus === "fulfilled") {
                    setActiveStep(2);
                    dispatch(getAllArticlesBeneficiars(beneficiaryId));
                }
                if (response.meta.requestStatus === "rejected") setHasSent(false);
            }
        }
        // After sending agreement (single or bulk) update a global sent status
        onSend();
    };

    const handleStep2Validation = (beneficiarList: IUser[]) => {
        const storedBeneficiaryValidation = JSON.parse(localStorage.getItem(`validation-list`) ?? "{}");
        const beneficiaryValidation = {
            modelName: allAgreementsTemplate?.find((template) => template.id == templateId)?.name || "",
            templateId,
            titleId: article?.titleId,
            titleName: (article as IArticle)?.titleName || (article as ITerm)?.title || "",
            beneficiaryName:
                (article as IArticle)?.beneficiary ||
                (article as ITerm)?.author ||
                `${beneficiar.surname.toUpperCase() ?? ""} ${beneficiar.name ?? ""}`.trim() ||
                "",
            reference: Math.floor(Math.random() * 1000000000),
            validationList: beneficiarList.map((beneficiar) => ({
                id: beneficiar.id,
                name: `${beneficiar.lastname?.toUpperCase() ?? ""} ${beneficiar.firstname ?? ""}`.trim(),
                email: beneficiar.email,
                avatarUrl: beneficiar.avatarUrl,
            })),
            creationDate: new Date(),
            userVariables,
        };
        storedBeneficiaryValidation[article!.id] = beneficiaryValidation;
        localStorage.setItem(`validation-list`, JSON.stringify(storedBeneficiaryValidation));
        setActiveStep(2);
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className="agreement-modal">
                <MultiStep
                    id="form"
                    activeStep={activeStep}
                    onStepChange={handleStepChange}
                    standaloneMoveBetweenSteps
                    title={t("Articles.ContractSignature")}
                    onClose={onClose}
                    fullscreen
                    steps={[
                        {
                            title: t("ContractSettings.Model"),
                            component: (
                                <AgreementModalStep1
                                    element={article}
                                    onModelChose={(templateId: number, userVariables?: UserVariables) =>
                                        handleStep1(templateId, userVariables)
                                    }
                                    from={from}
                                    inBulk={inBulk}
                                    bulkElements={articles}
                                    documentType={documentType}
                                    onTypeChange={setDocumentType}
                                    lockDocumentType={!!initialDocumentType}
                                />
                            ),
                        },
                        {
                            title: t("ContractSettings.Send"),
                            component: usesValidation ? (
                                <AgreementModalStep2Validation
                                    onCancel={onClose}
                                    onSend={(beneficiarList: IUser[]) => handleStep2Validation(beneficiarList)}
                                />
                            ) : (
                                <AgreementModalStep2
                                    article={article}
                                    beneficiary={beneficiar}
                                    email={email}
                                    onCancel={onClose}
                                    hasSent={hasSent}
                                    setHasSent={setHasSent}
                                    onSend={(email?: string) => (inBulk ? handleStep2() : handleStep2(email))}
                                    from={from}
                                    userVariables={userVariables}
                                    templateId={templateId}
                                    articles={articles}
                                    pageType={pageType}
                                    inBulk={inBulk}
                                />
                            ),
                        },
                        {
                            title: t("ContractSettings.Summary"),
                            component: (
                                <AgreementModalStep3
                                    article={article}
                                    beneficiary={beneficiar}
                                    onClose={onClose}
                                    from={from}
                                    usesValidation={usesValidation}
                                    inBulk={inBulk}
                                />
                            ),
                        },
                    ]}
                />
            </div>
        </Modal>
    );
};

interface AgreementModalStep1Props {
    element?: IArticle | ITerm;
    /**
     * Items for sending in bulk.
     */
    bulkElements?: IArticle[] | ITerm[];
    from: MainPortalType;
    onModelChose: (templateId: number, userVariables?: UserVariables) => void;
    /**
     * Whether sent in bulk or not.
     */
    inBulk: boolean;
    documentType: "agreement" | "avenant";
    onTypeChange: (type: "agreement" | "avenant") => void;
    lockDocumentType?: boolean;
}

const AgreementModalStep1 = ({
    element,
    from,
    onModelChose,
    bulkElements,
    inBulk,
    documentType,
    onTypeChange,
    lockDocumentType,
}: AgreementModalStep1Props) => {
    const { agreementsTemplate, agreementGenerated, defaultAgreementTemplate } = useAppSelector(
        (state) => state.agreements,
    );
    const { sendAgreementStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const navigate = useNavigate();
    const portalType = useContext(PortalType);
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [currentModelId, setCurrentModel] = useState(-1);
    const [modifiedVariables, setModifiedVariables] = useState<UserVariables>();
    const [modifiedVariablesCheck, setModifiedVariablesCheck] = useState<{
        [key: string]: string;
    }>();
    const [variableCount, setVariableCount] = useState(0);
    const [hasErrors, setHasErrors] = useState(false);
    const [currentFocusedInput, setCurrentFocusedInput] = useState(-1);

    const { variables: allVariables } = useAgreementVariables(portalType);

    const allAgreementsTemplate = useMemo(
        () =>
            defaultAgreementTemplate
                ? ([defaultAgreementTemplate, ...agreementsTemplate] as AgreementTemplate[])
                : null,
        [defaultAgreementTemplate, agreementsTemplate],
    );

    const filteredTemplates = useMemo(
        () => allAgreementsTemplate?.filter((t) => t.type === documentType) ?? null,
        [allAgreementsTemplate, documentType],
    );

    const getTemplateName = (template: AgreementTemplate) =>
        template?.id === defaultAgreementTemplate?.id ? t("ContractSettings.DefaultTemplate") : template?.name;

    const numberOfVariables = useMemo(() => {
        if (modifiedVariablesCheck == undefined) return 0;
        let count = 0;
        const identicalInputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
            `.agreement-step-1-choose-model-template-input`,
        );
        identicalInputs.forEach((input) => {
            if (input.value != "") count++;
        });
        return count;
    }, [modifiedVariablesCheck]);

    const onCustomVariablesChange = (e: any) => {
        const variableKey = e.target.getAttribute("variable-key");
        const variable = removeBrackets(variableKey);
        const value = e.target.value;
        setModifiedVariables((prev) => ({
            ...prev,
            [variable]: value,
        }));
        setModifiedVariablesCheck((prev) => ({
            ...prev,
            [e.target.dataset.index]: value,
        }));
        // Compare ids instead of building a `#id` selector: variable names may contain characters
        // (e.g. `{{test}}`) that make querySelectorAll throw.
        const identicalInputId = e.target.id;
        const identicalInputs = Array.from(
            document.querySelectorAll<HTMLInputElement>(".agreement-step-1-choose-model-template-input"),
        ).filter((input) => input.id === identicalInputId);

        identicalInputs.forEach((input) => {
            input.value = value;
            input.classList.remove("error");
            input.classList.remove("warning");
            input.classList.remove("success");
            if (value == "") {
                input.classList.remove("danger");
                input.classList.remove("success");
                input.classList.remove("warning");
            } else {
                input.classList.remove("warning");
                input.classList.remove("danger");
                input.classList.add("success");
            }
        });
        if (value.length > e.target.previousElementSibling.innerText.length && value.length < 60)
            e.target.style.width = `${value.length * 7.5}px`;
    };

    const agreementGeneratedWithInputs = useMemo(() => {
        if (!agreementGenerated || isObject(agreementGenerated)) return "";
        const variables: { key: string; name: string; index?: number }[] = [];
        /**
         * Now we match variables in span tags with data-text attribute containing a variable
         * example: <span data-text="{{myVariable}}" ...>{{myVariable}}</span>
         * This is more precise, like this we don't have to divide found variabe count value by two
         */
        for (const match of agreementGenerated.matchAll(SPAN_WITH_VARIABLE_REGEX)) {
            const variable = match?.[1] ?? "";
            variables.push({
                key: variable,
                name: allVariables?.[variable] ?? variable,
                index: match.index,
            });
        }

        setVariableCount(variables.length);

        const localAgreementGenerated = new DOMParser().parseFromString(agreementGenerated, "text/html");
        for (const variable of variables) {
            const $insertInputsIn = localAgreementGenerated.querySelectorAll(`[data-text="${variable.key}"]`);
            $insertInputsIn.forEach(($insertInputIn) => {
                $insertInputIn.innerHTML = `
                        <span class="agreement-step-1-choose-model-template-input-hidden-label" aria-hidden="true">
                            {{ ${variable.name} }}
                        </span>
                        <input
                            size="1"
                            id="agreement-template-${variable.name.replace(/ /g, "-")}"
                            data-index="${variable.index}"
                            class="agreement-step-1-choose-model-template-input"
                            type="text"
                            name="${variable.name}"
                            placeholder="${variable.name}"
                            variable-key="${variable.key}"
                        />
                    `;
            });
        }

        return localAgreementGenerated.documentElement.outerHTML;
    }, [agreementGenerated]);

    useEffect(() => {
        if (!agreementGeneratedWithInputs && sendAgreementStatus != "succeeded") return;
        const inputs = document.querySelectorAll(".agreement-step-1-choose-model-template-input");
        inputs.forEach((input) => {
            input.addEventListener("input", onCustomVariablesChange);
        });
        return () => {
            inputs.forEach((input) => {
                input.removeEventListener("input", onCustomVariablesChange);
            });
        };
    }, [agreementGeneratedWithInputs, sendAgreementStatus]);

    const validateInputs = () => {
        const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
            ".agreement-step-1-choose-model-template-input",
        );
        inputs.forEach((input) => {
            if (input.value == "") {
                input.classList.remove("success");
                input.classList.remove("warning");
                input.classList.add("error");
            }
        });
    };

    useEffect(() => {
        if (!hasErrors) return;
        validateInputs();
    }, [hasErrors]);

    useEffect(() => {
        dispatch(fetchAgreementsTemplate());
    }, []);

    const dispatchFetchGenerated = (templateId: number) => {
        if (!inBulk && element) {
            dispatch(fetchAgreementGenerated({ elementId: element.id, titleId: element.titleId, templateId, from }));
        }
        if (inBulk && bulkElements) {
            dispatch(
                fetchAgreementGenerated({
                    elementId: bulkElements[0].id,
                    titleId: bulkElements[0].titleId,
                    templateId,
                    from,
                }),
            );
        }
    };

    useEffect(() => {
        if (!filteredTemplates?.length) {
            setCurrentModel(-1);
            return;
        }
        const currentIsInFiltered = filteredTemplates.some((t) => t.id === currentModelId);
        if (currentIsInFiltered) return;
        const firstId = filteredTemplates[0].id;
        setCurrentModel(firstId);
        setModifiedVariables(undefined);
        setModifiedVariablesCheck(undefined);
        dispatchFetchGenerated(firstId);
    }, [filteredTemplates]);

    const handleModelChose = (id: number) => {
        setCurrentModel(id);
        setHasErrors(false);
        setModifiedVariables(undefined);
        setModifiedVariablesCheck(undefined);
        dispatchFetchGenerated(id);
    };

    const handleNextStep = () => {
        if (currentModelId == -1 || sendAgreementStatus != "succeeded") return;
        if (numberOfVariables < variableCount) {
            setHasErrors(true);
            validateInputs();
            return;
        }
        onModelChose(currentModelId, modifiedVariables);
    };

    const handleNextInput = () => {
        const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
            ".agreement-step-1-choose-model-template-input",
        );
        setCurrentFocusedInput((prev) => clamp((prev += 1), 0, inputs.length - 1));
        inputs.forEach((input, i) => {
            if (currentFocusedInput >= inputs.length - 1) setCurrentFocusedInput(0);
            if (i == currentFocusedInput) input.focus();
        });
    };

    return (
        <div className="agreement-step-1">
            {!inBulk && (
                <PanelTitle>
                    {t(
                        documentType === "avenant"
                            ? "ContractSettings.SelectModelAvenant"
                            : "ContractSettings.SelectModel",
                    )}
                </PanelTitle>
            )}
            {inBulk && bulkElements && bulkElements.length > 1 && (
                <div className="bulk-agreements-info">
                    <div className="header">
                        <Info color={variables["info"]} width="14" height="14" />
                        <SmallBodyHighlight color="info">{t("Agreements.GroupModeTitle")}</SmallBodyHighlight>
                    </div>
                    <div className="text">
                        <SmallBody>{t("Agreements.GroupModeText")}</SmallBody>
                    </div>
                </div>
            )}
            <div className="agreement-step-1-choose-model">
                <div className="agreement-step-1-choose-model-list">
                    <H3 color="grey" className="agreement-step-1-choose-model-head">
                        {t("ContractSettings.Model")}
                    </H3>
                    {!lockDocumentType && (
                        <ToggleButtonGroup
                            value={documentType}
                            exclusive
                            onChange={(_, val) => val && onTypeChange(val)}
                            size="small"
                            sx={{ mb: 1.5, "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.8rem" } }}
                        >
                            <ToggleButton value="agreement">{t("ContractSettings.TypeAgreement")}</ToggleButton>
                            <ToggleButton value="avenant">{t("ContractSettings.TypeAvenant")}</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {filteredTemplates?.map((template) => (
                        <div
                            className={`agreement-step-1-choose-model-element ${
                                template.id == currentModelId ? "active" : ""
                            }`}
                            onClick={() => handleModelChose(template.id)}
                            key={template.id}
                        >
                            <Radio
                                checked={template.id == currentModelId}
                                checkedIcon={<CheckCircleIcon />}
                                size="small"
                            />
                            {template.id == currentModelId ? (
                                <SmallBodyHighlight className="agreement-step-1-choose-model-element-text">
                                    {getTemplateName(template)}
                                </SmallBodyHighlight>
                            ) : (
                                <SmallBody color="grey" className="agreement-step-1-choose-model-element-text">
                                    {getTemplateName(template)}
                                </SmallBody>
                            )}
                        </div>
                    ))}
                    <div className="agreement-step-1-choose-model-new" onClick={() => navigate("/settings/contracts")}>
                        <Plus fill="var(--foreground)" />
                        {t("ContractSettings.NewModel")}
                    </div>
                </div>
                <div className="agreement-step-1-choose-model-template-wrapper">
                    {filteredTemplates !== null && filteredTemplates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8 py-12">
                            <NoteAddOutlinedIcon sx={{ fontSize: 52, opacity: 0.25 }} />
                            <SmallBodyHighlight color="grey">
                                {t("ContractSettings.NoAvenantTemplate")}
                            </SmallBodyHighlight>
                            <SmallBody color="grey">{t("ContractSettings.NoAvenantTemplateHint")}</SmallBody>
                        </div>
                    ) : (
                        <>
                            <div className="agreement-step-1-choose-model-template">
                                {sendAgreementStatus === "pending" ? (
                                    <div style={{ width: "100%", textAlign: "center" }}>
                                        <Spinner />
                                    </div>
                                ) : (
                                    agreementGenerated && (
                                        <>
                                            <div
                                                className="agreement-step-1-choose-model-template-content"
                                                dangerouslySetInnerHTML={{
                                                    __html: agreementGeneratedWithInputs,
                                                }}
                                            />
                                        </>
                                    )
                                )}
                            </div>
                            {sendAgreementStatus === "succeeded" && variableCount > 0 && (
                                <div
                                    className={`agreement-step-1-choose-model-template-footer ${
                                        hasErrors ? "error" : ""
                                    } ${numberOfVariables == variableCount ? "success" : ""}`}
                                >
                                    <SmallBodyHighlight
                                        color={
                                            numberOfVariables == variableCount
                                                ? "success"
                                                : !hasErrors
                                                  ? "warning"
                                                  : "danger"
                                        }
                                    >
                                        {numberOfVariables} / {variableCount} {t("ContractSettings.VariablesCompleted")}
                                    </SmallBodyHighlight>
                                    <SmallBodyHighlight
                                        color={
                                            numberOfVariables == variableCount
                                                ? "success"
                                                : !hasErrors
                                                  ? "warning"
                                                  : "danger"
                                        }
                                    >
                                        <SubdirectoryArrowRight onClick={handleNextInput} sx={{ cursor: "pointer" }} />
                                    </SmallBodyHighlight>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <StyledButton buttonType="primary" onClick={handleNextStep} fullWidth sx={{ width: "960px" }}>
                {t("NewElement.Continue")}
            </StyledButton>
        </div>
    );
};

interface AgreementModalStep2Props {
    article?: IArticle | ITerm;
    articles?: IArticle[] | ITerm[];
    beneficiary: IBeneficiar;
    email?: string;
    from: MainPortalType;
    userVariables?: UserVariables;
    templateId: number;
    onCancel: () => void;
    onSend: (email?: string) => void;
    inBulk: boolean;
    /**
     * Render loading spinner after sending contract for signature
     */
    hasSent: boolean;
    setHasSent: React.Dispatch<SetStateAction<boolean>>;
    /**
     * On press portal, from which page is launched the AgreementModal
     */
    pageType?: "publication" | "beneficiary" | "management";
}

const AgreementModalStep2 = (props: AgreementModalStep2Props) => {
    const {
        article,
        articles,
        beneficiary,
        email,
        from,
        userVariables,
        templateId,
        onCancel,
        onSend,
        inBulk,
        hasSent,
        setHasSent,
        pageType,
    } = props;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [emailField, setEmailField] = useState(email || "");
    const beneficiaryName =
        (article as IArticle)?.beneficiary ||
        (article as ITerm)?.author ||
        (article as ManagementArticle)?.beneficiary ||
        (beneficiary ? `${beneficiary.surname?.toUpperCase() ?? ""} ${beneficiary.name ?? ""}`.trim() : "") ||
        "";
    const titleName = (article as IArticle)?.titleName || (article as ITerm)?.title || "";

    const [emailToComplete, setEmailToComplete] = useState<IArticle | ITerm | null>(null);
    const [modalType, setModalType] = useState("");

    const handleSend = () => {
        onSend(emailField);
        setHasSent(true);
    };

    const handleFileDownload = (article: IArticle | ITerm) => {
        const bulkBeneficiaryName = (article as IArticle).beneficiary || (article as ITerm).author;
        const bulkTitleName = (article as IArticle)?.titleName || (article as ITerm)?.title;

        const filename = inBulk
            ? getStatementFilename(bulkBeneficiaryName ?? "", bulkTitleName ?? "", t("Summary.Contract"), new Date())
            : getStatementFilename(beneficiaryName ?? "", titleName ?? "", t("Summary.Contract"), new Date());

        dispatch(
            fetchContractDownload({
                titleId: article.titleId ?? 0,
                articleId: article.id,
                download: true,
                filename,
                from,
                userVariables,
                templateId,
            }),
        );
    };

    // Needed for the table in the bulk recap.
    const articlesWithNoEmail = useMemo(() => {
        return (articles as IArticle[])?.filter(
            (article: IArticle) => (article as IArticle).email === "" || (article as IArticle).email === null,
        );
    }, [articles]);

    const toSendAgreementNumber = articles && articles?.length - articlesWithNoEmail.length;

    const bulkFooterContent = (
        <div className="info-footer">
            <div className="info-footer-text flex">
                <span>
                    {toSendAgreementNumber}{" "}
                    {toSendAgreementNumber !== 1 ? t("Agreements.ContractsToSend") : t("Agreements.ContractToSend")}
                </span>
                <span className="red">
                    {articlesWithNoEmail?.length !== 0
                        ? `- ${articlesWithNoEmail?.length} ${t("Agreements.BeneficiaryWithNoEmail")}`
                        : ""}
                </span>
            </div>
        </div>
    );

    // Open modal to enter email if there are none.
    const openEmailModal = (article: IArticle | undefined) => {
        article && setEmailToComplete(article);
        setModalType("email");
    };

    const renderEmail = (email: string | null, articleId: number | undefined) => {
        const currentArticle = (articles as IArticle[])?.find((article) => article.id === articleId);
        return (
            <div>
                {email === "" || email === null ? (
                    <TextButton
                        text={t("Agreements.EnterEmail")}
                        handleClick={() => openEmailModal(currentArticle as IArticle)}
                        underlineOnHover={true}
                    />
                ) : (
                    <span>{email}</span>
                )}
            </div>
        );
    };

    const renderDownloadButton = (articleId: number | undefined) => {
        const currentArticle = (articles as IArticle[])?.find((article: IArticle) => article.id === articleId);
        return (
            <Tooltip title={t("Agreements.DownloadPreview")} arrow>
                <span>
                    <OutlinedFile onClick={() => currentArticle && handleFileDownload(currentArticle)} />
                </span>
            </Tooltip>
        );
    };

    // Columns for the table in bulk recap.
    const bulkColumns: LastElementTableColumns[] = [
        { id: "name", label: "Article", align: "left", sort: false },
        { id: "titleName", label: t("Agreements.Publication"), align: "left" },
        { id: "beneficiary", label: t("Agreements.Beneficiary"), align: "left" },
        {
            id: "email",
            label: "Email",
            align: "left",
            format: (value: any, id?: number) => renderEmail(value, id),
        },
        {
            id: "download",
            label: "",
            format: (value: any, id?: number) => renderDownloadButton(id),
        },
    ];

    return (
        <div className="agreement-step-2">
            <ModalBox
                open={modalType === "email"}
                onClose={() => setModalType("")}
                mainTitle={(emailToComplete as IArticle)?.beneficiary || (emailToComplete as ITerm)?.author}
                subTitle={t("Agreements.EmailModalSubtitle")}
            >
                <EmailModal
                    article={emailToComplete}
                    handleClose={() => setModalType("")}
                    beneficiaryId={beneficiary.id}
                    pageType={pageType}
                />
            </ModalBox>
            <div className={`agreement-step-2-body ${inBulk ? "bulk" : ""}`}>
                {hasSent ? (
                    <div style={{ width: "100%", textAlign: "center" }}>
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <div className="agreement-step-2-head">
                            <Heading2>{t("Articles.SendSignature")}</Heading2>
                            <>
                                {inBulk && articles && articles.length > 0 ? (
                                    <Body color="grey">{t("Agreements.BulkSignatureStep2Subtitle_1")}</Body>
                                ) : (
                                    <Body color="grey">
                                        {beneficiaryName} {t("ContractSettings.SignatureStep2Subtitle")}
                                    </Body>
                                )}
                                <Body color="grey">{t("Agreements.BulkSignatureStep2Subtitle_2")}</Body>
                            </>
                        </div>
                        <div className="agreement-step-2-email">
                            {!inBulk && (
                                <>
                                    <>
                                        <H3 color="grey">
                                            {t("Articles.EmailOf")} {beneficiaryName}
                                        </H3>
                                        <StyledTextField
                                            value={emailField}
                                            onChange={(e: any) => setEmailField(e.target.value.replace(/\s/g, ""))}
                                            fullWidth
                                        />
                                    </>
                                    <FileButton
                                        label={t("ContractSettings.DownloadPreviewFile")}
                                        onClick={() => article && handleFileDownload(article)}
                                        style={{ width: "100%" }}
                                    />
                                </>
                            )}
                            {inBulk && (
                                <div>
                                    <LastElementTable
                                        data={articles}
                                        columns={bulkColumns}
                                        max={9999}
                                        maxHeight={300}
                                        bottomFooterContent={bulkFooterContent}
                                    />
                                    {articlesWithNoEmail.length > 0 && (
                                        <div className="email-missing-warning">
                                            <AlertTriangle color="#848B4F" />
                                            <SmallBody color="warning">
                                                {t("Agreements.BeneficiaryEmailWarning")}
                                            </SmallBody>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="agreement-step-2-action">
                            <StyledButton buttonType="secondary" onClick={onCancel}>
                                {t("ContractSettings.Cancel")}
                            </StyledButton>
                            <StyledButton
                                buttonType="primary"
                                disabled={(!inBulk && emailField == "") || (inBulk && toSendAgreementNumber === 0)}
                                onClick={handleSend}
                            >
                                {t("Articles.SendForSignature")}
                            </StyledButton>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

interface AgreementModalStep2ValidationProps {
    onCancel: () => void;
    onSend: (beneficiarList: IUser[]) => void;
}

const AgreementModalStep2Validation = (props: AgreementModalStep2ValidationProps) => {
    const { onCancel, onSend } = props;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { data: users } = useAppSelector((state) => state.adminPanel.users.company);
    const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<IUser[]>([]);

    const { handleQuery: handleQueryBeneficiaries, setOffsetPagination: setOffsetPaginationBeneficiaries } =
        useAPIPagination({
            type: "beneficiaries",
            defaultLimitPagination: 10,
            portal: "press",
            autocomplete: true,
        });
    const renderInputBeneficiaries = (params: any) => (
        <StyledTextField
            {...params}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQueryBeneficiaries(e)}
        />
    );
    const renderOptionTextBeneficiar = (option: any) => `${option.name ?? ""} ${option.surname}`.trim();

    useEffect(() => {
        dispatch(getUserListByCompany());
    }, []);

    const renderOption = (props: any, option: any, type: "publication" | "beneficiar") => {
        const idList = selectedBeneficiaries.map((benef) => benef.id);
        return option.addElement ? (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li
                {...props}
                key={option.id}
                className={`autocomplete-beneficiaries${idList.includes(option.id) ? " selected" : ""}`}
            >
                <div className="autocomplete-beneficiaries-left-content">
                    <Avatar
                        variant="circular"
                        sx={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#EEF3FD",
                            color: variables["foreground"],
                            fontSize: "14px",
                        }}
                    >
                        {getInitials(`${option.firstname ?? ""} ${option.lastname ?? ""}`)}
                    </Avatar>
                    <span>{`${option.firstname ?? ""} ${option.lastname ?? ""}`}</span>
                </div>
                <div className="autocomplete-beneficiaries-right-content">
                    <span>{option.email ?? ""}</span>
                </div>
            </li>
        );
    };

    const renderAutocompleteTags = (options: any, getTagProps: any) => {
        const limit = 2;
        return (
            <div className="chips-list">
                {options.slice(0, limit).map((option: any, index: number) => {
                    const tagColor = tagsColors.find((color: any) => color.id === option.color);
                    return (
                        <>
                            <Chip
                                {...getTagProps({ index })}
                                className="autocomplete-chip"
                                avatar={option.avatarUrl ? <Avatar src={option.avatarUrl} /> : undefined}
                                label={option.firstname || option.lastname}
                                sx={{
                                    background: `${tagColor?.backgroundColor}`,
                                    color: `${tagColor?.textColor}`,
                                }}
                            />
                            {index == limit - 1 && selectedBeneficiaries.length - limit > 0 && (
                                <span className="autocomplete-input-number">{`+${
                                    selectedBeneficiaries.length - limit
                                }`}</span>
                            )}
                        </>
                    );
                })}
            </div>
        );
    };

    const handleOnScrollEnd = () => setOffsetPaginationBeneficiaries(users?.length ?? 0);

    const handleSend = () => {
        onSend(selectedBeneficiaries);
    };

    const handleMoveTo = (from: number, to: number) => {
        setSelectedBeneficiaries((prev) => {
            const element = prev[from];
            prev.splice(from, 1);
            prev.splice(to, 0, element);
            return [...prev];
        });
    };

    return (
        <div className="agreement-step-2 validation">
            <div className="agreement-step-2-body">
                <div className="agreement-step-2-head">
                    <Heading2>{t("ContractValidation.ValidationChain")}</Heading2>
                    <Body color="grey">{t("ContractValidation.ValidationChainSubtitle")}</Body>
                </div>
                <div className="agreement-step-2-beneficiaries">
                    <AutocompleteFormComponent
                        label={
                            <H3 color="grey" style={{ paddingLeft: 0 }}>
                                {t("ContractValidation.SelectBeneficiaries")}
                            </H3>
                        }
                        options={users}
                        value={selectedBeneficiaries}
                        onChange={(e: any, value: any) => setSelectedBeneficiaries(value)}
                        renderInput={renderInputBeneficiaries}
                        renderOptionText={renderOptionTextBeneficiar}
                        renderOption={(props: any, option: any) => renderOption(props, option, "beneficiar")}
                        renderTags={renderAutocompleteTags}
                        // addElement={t("NewArticle.NewBeneficiary")}
                        onScrollEnd={() => handleOnScrollEnd()}
                        multiple
                        limitTags={2}
                    />
                </div>
                {selectedBeneficiaries.length > 0 && (
                    <div className="agreement-step-2-selected-beneficiary">
                        <InfoBlue
                            icon={<></>}
                            info={<Body color="info">{t("ContractValidation.FollowingOrder")}</Body>}
                        />
                        <div className="agreement-step-2-selected-beneficiary-list">
                            {selectedBeneficiaries.map((beneficiar: any, index) => (
                                <div className="agreement-step-2-selected-beneficiary-element" key={index}>
                                    <div className="left">
                                        <Avatar
                                            src={beneficiar.avatarUrl}
                                            variant="circular"
                                            sx={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: "#EEF3FD",
                                                color: variables["foreground"],
                                                fontSize: "14px",
                                            }}
                                        >
                                            {beneficiar.firstname
                                                ? beneficiar.firstname?.slice(0, 1)
                                                : beneficiar.lastname?.slice(0, 1)}
                                        </Avatar>
                                        <Body>{`${beneficiar.firstname ?? ""} ${beneficiar.lastname ?? ""}`}</Body>
                                    </div>
                                    <SmallBody color="grey">{beneficiar.email ?? ""}</SmallBody>
                                    <div
                                        className={`arrow-up ${index > 0 ? "" : "disabled"}`}
                                        onClick={() => handleMoveTo(index, index - 1)}
                                    ></div>
                                    <div
                                        className={`arrow-down ${
                                            index < selectedBeneficiaries.length - 1 ? "" : "disabled"
                                        }`}
                                        onClick={() => handleMoveTo(index, index + 1)}
                                    ></div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="agreement-step-2-action">
                    <StyledButton buttonType="secondary" onClick={onCancel}>
                        {t("ContractSettings.Cancel")}
                    </StyledButton>
                    <StyledButton buttonType="primary" onClick={handleSend}>
                        {t("ContractValidation.Validate")} {selectedBeneficiaries[0]?.firstname ?? ""}
                    </StyledButton>
                </div>
            </div>
        </div>
    );
};

interface AgreementModalStep3Props {
    beneficiary: IBeneficiar;
    article?: IArticle | ITerm;
    from: "press" | "book" | "audiovisual";
    usesValidation?: boolean;
    onClose: () => void;
    inBulk: boolean;
}

const AgreementModalStep3 = (props: AgreementModalStep3Props) => {
    const { beneficiary, article, from, usesValidation = false, onClose, inBulk } = props;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { contract } = useAppSelector((state) => state.statements);

    // download only when the contract has been sent, so that we're sure the contract is generated in PDF
    const isReadyToDownload = useMemo(() => {
        return ["audiovisual", "press"].includes(from)
            ? (article as IArticle)?.contractSigned != undefined && (article as IArticle)?.contractSigned > 0
            : true;
    }, [article]);

    const beneficiaryName = (article as IArticle)?.beneficiary || (article as ITerm)?.author || "";
    const titleName = (article as IArticle)?.titleName || (article as ITerm)?.title || "";

    useEffect(() => {
        article &&
            dispatch(
                fetchContract({
                    titleId: article.titleId ?? 0,
                    articleId: article.id,
                    from,
                    download: true,
                }),
            );
    }, []);

    const handleFileDownload = () => {
        if (!isReadyToDownload) return;
        const filename = getStatementFilename(
            beneficiaryName ?? "",
            titleName ?? "",
            t("Summary.Contract"),
            new Date(),
        );
        const linkSource = `data:application/pdf;base64,${contract}`;
        downloadFile({ source: linkSource, fileName: filename, isLink: true });
    };

    return (
        <div className="agreement-step-3">
            <div className="agreement-step-3-body">
                <div className="agreement-step-3-checkmark">
                    <Checkmark color="white" width="20" height="20" />
                </div>
                <div className="agreement-step-3-head">
                    <Heading1>
                        {usesValidation
                            ? t("ContractSettings.SignatureStep3ValidationTitle")
                            : t("ContractSettings.SignatureStep3Title")}
                    </Heading1>
                    <Body>
                        {usesValidation
                            ? t("ContractSettings.SignatureStep3ValidationSubtitle")
                            : !inBulk
                              ? `${t("ContractSettings.SignatureStep3Subtitle", {
                                    name: beneficiaryName,
                                })} ${beneficiary.email || ""}`
                              : t("Agreements.BulkSignatureStep3Subtitle")}
                    </Body>
                </div>
                <div className="agreement-step-3-download">
                    {!usesValidation && (
                        <FileButton
                            label={t("ContractSettings.SignatureStep3Download")}
                            onClick={() => handleFileDownload()}
                            disabled={!isReadyToDownload}
                            style={{ width: "100%" }}
                        />
                    )}
                </div>
                <div className="agreement-step-3-action">
                    <StyledButton buttonType="primary" fullWidth onClick={onClose}>
                        {t("PaymentModal.ButtonFinish")}
                    </StyledButton>
                </div>
            </div>
        </div>
    );
};

export default AgreementModal;
